/**
 * Что раздел «Склад» спрашивает у сервера — этап P5 (§2 ТЗ портала).
 *
 * «Аудит» и «Цены закупки» — `ops-api` (`services/ops/api/routers/portal_stock.py`);
 * «Блюда и товары» — `finance-api` (`services/finance/api/routers/portal_stock.py`):
 * сопоставление блюд кассы с товарами склада принадлежит финансовому агенту (M2).
 */

import { request } from '../../api';

export interface Audit {
  synced_at: string | null;
  missing: string[];
  goods_read: boolean;
  remains_read: boolean;
  counts: { units: number; negatives: number; duplicates: number; total: number };
  units: { good_id: string; name: string; problem: string }[];
  negatives: {
    good_id: string;
    name: string;
    quantity: string;
    unit: string | null;
    department: string | null;
  }[];
  duplicates: { names: string[]; good_ids: string[] }[];
}

export interface PriceChange {
  goods_id: string;
  goods: string | null;
  unit: string | null;
  supplier: { id: number; name: string };
  previous: { date: string; price: string };
  last: { date: string; price: string; invoice_id: number };
  change_pct: string;
  above_threshold: boolean;
}

export interface Prices {
  since: string;
  days: number;
  threshold_pct: string;
  all: boolean;
  compared: number;
  count: number;
  items: PriceChange[];
}

export interface Sold {
  quantity: number;
  amount: string;
}

export interface Goods {
  goods_id: string;
  name: string;
  code: string | null;
  unit: string | null;
}

export interface Matching {
  days: number;
  counts: {
    dishes: number;
    matched: number;
    ambiguous: number;
    unmatched: number;
    rejected_skipped: number;
  };
  ambiguous: { item_id: number; name: string; code: string | null; sold: Sold; candidates: Goods[] }[];
  unmatched: { item_id: number; name: string; code: string | null; sold: Sold }[];
  decisions: {
    item_id: number;
    item_name: string;
    goods_id: string;
    goods_name: string | null;
    status: 'confirmed' | 'rejected' | string;
    decided_at: string | null;
    decided_by: string | null;
  }[];
}

export interface MatchingAnswer {
  message: string;
  matching: Matching;
}

/** Полями объекта: тест подменяет ровно один адрес. */
export const stockApi = {
  audit: () => request<Audit>('/api/ops/stock/audit'),
  prices: (days: number, all: boolean) =>
    request<Prices>(`/api/ops/stock/prices?days=${days}${all ? '&all=true' : ''}`),
  matching: () => request<Matching>('/api/finance/portal/stock/matching'),
  goods: (q: string) =>
    request<Goods[]>(`/api/finance/portal/stock/goods?q=${encodeURIComponent(q)}`),
  confirm: (itemId: number, goodsId: string) =>
    request<MatchingAnswer>(`/api/finance/portal/stock/matching/${itemId}/confirm`, {
      method: 'POST',
      body: { goods_id: goodsId },
    }),
  reject: (itemId: number, goodsId: string) =>
    request<MatchingAnswer>(`/api/finance/portal/stock/matching/${itemId}/reject`, {
      method: 'POST',
      body: { goods_id: goodsId },
    }),
};
